import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { backendService } from "../../services/backend.service";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import type { NurseShiftLineageEntry } from "./shiftLineage";

const SHIFT_CHANGE_LOG_QUERY_KEY = "shift-change-log";

/**
 * Every sync decision about ONE shift — the focused read behind the link from a shift's raw punches.
 *
 * Deliberately keyed on the shift id rather than on the nurse + a date range: the nurse-scoped list
 * filters by when a decision was RECORDED, so a shift re-synced months later (a re-sent payroll file, a
 * quarter backfill) sits outside any window a caller could guess. Same row shape as
 * `useNurseShiftLineageQuery`, so one view renders either.
 */
export const useShiftChangeLogQuery = (shiftId: string | undefined): UseQueryResult<NurseShiftLineageEntry[], Error> => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	return useQuery({
		queryKey: [SHIFT_CHANGE_LOG_QUERY_KEY, chosenDepartment?.department_id, shiftId],
		queryFn: async () => {
			// `enabled` already gates on both, but TS can't see that — assert rather than cast, so a
			// future caller that drops the gate fails loudly instead of requesting `.../shifts/undefined`.
			if (!shiftId) throw new Error("useShiftChangeLogQuery ran without a shift id");
			trackEvent("shift_change_log_fetch_start", { department_id: chosenDepartment?.department_id, shift_id: shiftId });

			const { data } = await backendService.getShiftChangeLog(chosenDepartment.department_id, shiftId);

			trackEvent("shift_change_log_fetch_success", { department_id: chosenDepartment?.department_id, shift_id: shiftId, entry_count: data.length });
			return data;
		},
		staleTime: 60 * 1000,
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id && !!shiftId,
		meta: {
			onError: (error: unknown) => {
				showErrorSnackbar(t("employeeChangelog.shiftLineage.focus.loadError"), { action: "failed to fetch shift change log" }, error);
				trackErrorEvent("shift_change_log_fetch_error", error, {
					department_id: chosenDepartment?.department_id,
					shift_id: shiftId,
					error_source: "API",
				});
			},
		},
	});
};
